import { keyBy } from 'lodash';
import Notifications from 'react-notification-system-redux';
import { APIScenarioReturn } from '../../../general/apiSagaFactory';
import { api } from '../../../general/apiScenarios';
import { INode, NodeFlag, NodeType } from '../../../general/model';
import { makeCreativeFolder } from '../../../general/modelCreativeFolder';
import * as filterActions from '../../../general/modules/filter/actions';
import { pagePathChanged } from '../../../general/modules/router/actions';
import { CHNodeIsCreativeFolder, NodePathParams, getParent } from '../../../general/node';
import { ListenerApiLight, _select, _take, lm } from '../../listenerMiddleware';
import { Page } from '../../models/page';
import { nodeIDChanged } from '../app/actions';
import { getNodeId, getPage, getShareID, getUser } from '../app/selectors';
import { loadBreadcrumbsSuccess } from '../breadcrumbs/actions';
import { getCurrentNode, getRelativeLocation } from '../breadcrumbs/selectors';
import * as searchActions from '../search/actions';
import { loadSearch } from '../search/listeners';
import * as fileManagerActions from './actions';
import { columnIsAvailableHere } from './getColumns';
import { Column, FileManagerRow, Messages } from './model';
import { secondaryColumnsByKey } from './secondaryColumns';
import { getSelectedRange } from './selectors/getTableData';
import { getColumnToggles, getNodes, getTeams } from './selectors/selectors';
import { detectBLContext } from '../creativeWorkflow/legacy_sagas/pureSagas';
import * as appActions from '../app/actions';

export function fileManagerListeners() {
  watchLoadRefresh();
  watchColumnToggled();
  watchAppNodeIdChanged();
  preloadBlTeams();
  watchSelectorChanged();
  watchFilemanagerSettled();

  lm.takeEvery(fileManagerActions.loadGenresRequested, async (action, lapi) => {
    const genres = await api.bl.loadBLGenres(action.payload.project_id);
    lapi.signal.throwIfAborted();
    if (!genres.success) return;
    lapi.dispatch(
      fileManagerActions.loadGenresUpdated({
        genres: genres.data.response,
      }),
    );
  });
  lm.takeEvery(fileManagerActions.loadIntrosRequested, async (action, lapi) => {
    const intros = await api.bl.loadBLIntros(action.payload.project_id);
    lapi.signal.throwIfAborted();
    if (!intros.success) return;
    lapi.dispatch(
      fileManagerActions.loadIntrosUpdated({
        intros: intros.data.response,
      }),
    );
  });
}

function deriveParentID(
  lapi: ListenerApiLight,
  nodeID: number,
): { id: number; name: string } | null {
  const page = _select(lapi, getPage);
  if (page === Page.FOLDER || page === Page.SHARED_FOLDER) {
    const node = _select(lapi, getCurrentNode);
    return node;
  } else if (page === Page.SEARCH) {
    const nodes = _select(lapi, getNodes) as (FileManagerRow & NodePathParams)[];
    const nodeByID = keyBy(nodes, (x) => x.id);
    const node = nodeByID[nodeID];
    if (node === undefined) return null;

    return getParent(node);
  } else {
    return null;
  }
}

function watchSelectorChanged() {
  lm.takeEvery(fileManagerActions.selectorChanged, (a, lapi) => {
    const { node, shift, isChecked } = a.payload;
    const folder = deriveParentID(lapi, node.id);
    if (folder === null) return;
    if (shift) {
      lapi.dispatch(fileManagerActions.lastNodeIDSelectedChanged({ nodeID: node.id }));
      const range = _select(lapi, getSelectedRange);
      if (range) {
        lapi.dispatch(
          fileManagerActions.selectedNodesChanged({
            isSelected: true,
            folder,
            nodes: range,
            replace: true,
          }),
        );
      }
    } else {
      lapi.dispatch(fileManagerActions.firstNodeIDSelectedChanged({ nodeID: node.id }));
      lapi.dispatch(
        fileManagerActions.selectedNodesChanged({
          isSelected: isChecked,
          nodes: [node],
          folder,
          replace: false,
        }),
      );
    }
  });
}

function watchAppNodeIdChanged() {
  lm.takeEvery(nodeIDChanged, function (action, api) {
    // TODO: node id changed, page path changed, see other TODOs with this text
    // const page=  _select(getPage);

    // if (page === Page.FOLDER) {
    api.dispatch(fileManagerActions.loadNodesRequested({ nodeID: action.payload.nodeID }));
    api.dispatch(filterActions.allFiltersRemoved());
    // }
  });
}

function watchLoadRefresh() {
  lm.takeLatest(
    [
      fileManagerActions.loadNodesRequested,
      searchActions.loadSearchRequested,
      fileManagerActions.refreshRequested,
    ],
    async function (action, lapi) {
      if (action.type === fileManagerActions.refreshRequested.type) {
        const appNodeID = _select(lapi, getNodeId);
        if (appNodeID) {
          await loadNodes(lapi, appNodeID);
          if (lapi.signal.aborted) return;
        }
      }
      if (action.type === fileManagerActions.loadNodesRequested.type) {
        await loadNodes(lapi, action.payload.nodeID);
        if (lapi.signal.aborted) return;
      }
      if (action.type === searchActions.loadSearchRequested.type) {
        await loadSearch(lapi, action.payload);
        if (lapi.signal.aborted) return;
      }
    },
  );
}

async function loadNodes(lapi: ListenerApiLight, nodeID: number) {
  lapi.dispatch(fileManagerActions.loadNodesStarted());

  const result = await apiLoadNodes2(lapi, nodeID);
  if (lapi.signal.aborted) return;
  if (!result.success) {
    lapi.dispatch(
      fileManagerActions.loadNodesError({ error: 'error', loadedFolderNodeID: nodeID }),
    );
    lapi.dispatch(Notifications.error(Messages.loadNodesError()));
    return false;
  }

  const unknownNode = result.data.response.nodes.find(
    (node) => !Object.values(NodeType).map(String).includes(node.type),
  );
  if (unknownNode) {
    lapi.dispatch(
      fileManagerActions.loadNodesError({
        loadedFolderNodeID: nodeID,
        error: {
          message: Messages.loadNodesInvalid().message,
          data: unknownNode,
        },
      }),
    );
    lapi.dispatch(Notifications.error(Messages.loadNodesInvalid()));
    return false;
  }

  lapi.dispatch(
    fileManagerActions.loadNodesSuccess({
      result: result.data.response.nodes,
      loadedFolderNodeID: nodeID,
    }),
  );
  return true;
}

export async function apiLoadNodes2(
  lapi: ListenerApiLight,
  nodeID: number,
): Promise<APIScenarioReturn<{ nodes: INode[] }>> {
  const page = _select(lapi, getPage);
  if (page === Page.SHARED_FOLDER) {
    const shareId = _select(lapi, getShareID);
    if (shareId === null) throw new Error('Share ID = null');
    return await api.fs.loadShareNodes(shareId, nodeID);
  } else {
    return await api.fs.loadNodes(nodeID);
  }
}
async function updateMisleadLevels(lapi: ListenerApiLight) {
  // берем отсортированные ноды, таким образом загрузка будет сверху вниз
  const nodes = _select(lapi, getNodes);
  const creatives = nodes.filter(CHNodeIsCreativeFolder);
  const ids = creatives.map((x) => x.id);
  if (ids.length === 0) return;
  lapi.dispatch(fileManagerActions.blLoadingChanged({ blLoading: true }));
  const creativeObjects = await api.bl.loadCreativeDataHub(ids);
  if (lapi.signal.aborted) return;
  if (creativeObjects.success) {
    const teams = _select(lapi, getTeams);
    if (teams === null) {
      await _take(lapi, fileManagerActions.blTeamsLoaded);
    }
    if (lapi.signal.aborted) return;
    const keyedTeams = keyBy(teams, (t) => t.id);
    const creatives = creativeObjects.data.response.map((cr) => makeCreativeFolder(cr, keyedTeams));
    lapi.dispatch(fileManagerActions.creativeInfoUpdated({ creatives }));
  }
  lapi.dispatch(fileManagerActions.blLoadingChanged({ blLoading: false }));
}

function watchColumnToggled() {
  lm.takeLatest(fileManagerActions.columnToggled, async (action, lapi) => {
    const toggles: ReturnType<typeof getColumnToggles> = _select(lapi, getColumnToggles);
    if (
      toggles[Column.mislead_level] === true &&
      action.payload.columnID === Column.mislead_level
    ) {
      // eslint-disable-next-line @myua/rtk-helper/check-signal-aborted
      await updateMisleadLevels(lapi);
    }
  });
}

function watchFilemanagerSettled() {
  async function callback(lapi: ListenerApiLight) {
    // здесь мы запрашиваем данные о креативах для загруженных нод.
    let nodes: FileManagerRow[] = _select(lapi, getNodes);

    const relativeLocation = _select(lapi, getRelativeLocation);
    const page = _select(lapi, getPage);

    // eligible -- мы находимся внутри тегированной папки, где все ноды -- креативы
    // в этом случае мы просто запрашиваем данные по всем папкам
    const convStateColumn = secondaryColumnsByKey[Column.conversion_status];
    const eligible = columnIsAvailableHere(convStateColumn, relativeLocation);

    // в таких случаях как страница поиска у нас нет нормальных breadcrumbs, а
    // значит проверить eligible мы не можем и он будет false
    // один из случаев когда такое не происходит -- страница поиска, и тут мы
    // должны проверить каждую ноду на eligible-ность и запросить данные только
    // для подходящих

    // если данные по бредкрамбсам еще не загрузились, то они загрузятся позже и эта сага
    // стриггерится снова

    if (!eligible) {
      if (page === Page.SEARCH) {
        nodes = nodes.filter((node) => {
          return node.flags.includes(NodeFlag.folder_creative);
        });
      } else {
        return;
      }
    }
    if (nodes.length === 0) return;
    // берем отсортированные ноды, таким образом загрузка будет сверху вниз
    const user = _select(lapi, getUser);
    if (user === null) return;

    await updateMisleadLevels(lapi);
    if (lapi.signal.aborted) return;
    const nodeID = _select(lapi, getNodeId);
    if (nodeID) {
      const ctx = await detectBLContext(lapi, nodeID).project();
      if (lapi.signal.aborted) return;

      lapi.dispatch(fileManagerActions.loadGenresRequested({ project_id: ctx.id }));
      lapi.dispatch(fileManagerActions.loadIntrosRequested({ project_id: ctx.id }));
    }

    const ids = nodes.map((n) => n.id);
    const result = await api._unsorted.loadCreativeConversionStatusBulk(ids);
    if (lapi.signal.aborted) return;
    if (result.success) {
      lapi.dispatch(fileManagerActions.conversionStatusUpdated(result.data.response));
    }
  }

  lm.takeLatest(
    [loadBreadcrumbsSuccess, fileManagerActions.loadNodesSuccess, searchActions.loadSearchSuccess],
    async (_, lapi) => {
      await callback(lapi);
      lapi.signal.throwIfAborted();
    },
  );
}

function preloadBlTeams() {
  lm.takeOnce(appActions.initListeners, async (_, lapi) => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const action = await _take(lapi, pagePathChanged);
      if (action.payload.page !== Page.SHARED_FOLDER) {
        break;
      }
    }
    if (lapi.signal.aborted) return;
    const blTeams = await api.bl.getBlTeams();
    if (lapi.signal.aborted) return;
    if (blTeams.success === false) {
      lapi.dispatch(Notifications.error({ title: "Can't obtain teams list" }));
      lapi.dispatch(fileManagerActions.blTeamsLoaded({ teams: [] }));
      return;
    }
    lapi.dispatch(fileManagerActions.blTeamsLoaded({ teams: blTeams.data.response }));
  });
}
